/* eslint jsx-a11y/anchor-is-valid: 0 */

import React from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import {
  getPost,
  pageVisit,
  getPostBySlug,
  getComments,
  createComment,
  createLike,
  removeLike,
  createLikeOnPost,
  deleteLikeOnPost,
  getFovuritePosts,
  createFavoritePost,
  removeFavoritePost,
  createPublicAccess,
  getPublicAccessCheck,
  removeComment,
} from "api";
import { withRouter } from "../../hooks/withRouter";
import {
  captureAffiliateFromSearch,
  getStoredAffiliateKey,
  getStoredMarketingKey,
} from "../../utils/affiliateTracking";
import JoditEditor from "jodit-react";
import CommentBlock from "../Post/CommentBlock";
import CommentReplies from "../Post/CommentReplies";
import CommentAvatar from "../Post/CommentAvatar";
import PostDetailMessageCircleIcon from "./PostDetailMessageCircleIcon";
import PostDetailSendIcon from "./PostDetailSendIcon";
import { PostDetailReplyIcon } from "./PostDetailCommentIcons";
import "../Post/Comments.scss";
import "./PostDetail.feed.scss";
import NotificationService from "components/alerts/index";
import PostDetailContent, {
  PostDetailHeaderActions,
} from "./PostDetailContent";
import { bindPostDetailWistia } from "./wistiaPostPlayer";
import { showPostDetailToast } from "./postDetailToast";
import { HomepageThemeScope } from "../Homepage/styles";
import {
  getBackNavigation,
  getPostRouteParams,
  shouldShowComments,
} from "./postDetailUtils";
import {
  PostDetailArticle,
  PostDetailBackLink,
  PostDetailBreadcrumb,
  PostDetailBreadcrumbCurrent,
  PostDetailBreadcrumbSep,
  PostDetailCommentsSlot,
  PostDetailLoading,
  PostDetailMain,
  PostDetailNav,
} from "./PostDetail.styles";

const editorConfig = {
  uploader: {
    url: `${process.env.REACT_APP_API_URL}media/image`,
    isSuccess: function () {
      return true;
    },
    process: function (resp) {
      return {
        files: [resp.url],
        path: resp.url,
        baseurl: resp.url,
        error: false,
        message: "",
      };
    },
    defaultHandlerSuccess: function (data) {
      const editor = this.jodit || this;
      editor.selection.insertImage(data.baseurl);
    },
  },
  useSearch: false,
  spellcheck: false,
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  buttons: "bold,italic,image",
  buttonsMD: "bold,italic,image",
  buttonsSM: "bold,italic,image",
  buttonsXS: "bold,italic,image",
  toolbarAdaptive: false,
  toolbarSticky: false,
  theme:
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "light"
      ? "default"
      : "dark",
  // Keep popups on body so they aren't clipped by the compose card.
  popupRoot:
    typeof document !== "undefined" ? document.body : undefined,
  addNewLine: false,
  addNewLineOnDBLClick: false,
  // Avoid Jodit "Paste Storage" popup in comments.
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: "insert_clear_html",
  minHeight: 70,
  placeholder: "Ask a question or share your read on the market…",
  readOnly: false,
};

/** Jodit empty states are `<p><br></p>`. An image alone still counts as content. */
const hasComposeContent = (html) => {
  if (!html) return false;
  const raw = String(html);
  if (/<img\b/i.test(raw)) return true;
  const text = raw
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;/gi, " ")
    .trim();
  return text.length > 0;
};

const commentTime = (comment) => {
  const time = new Date(comment && comment.createdAt).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const SUPPORT_LAUNCHER_CLASS = "hide-support-launcher";

/** Zendesk launcher sits on the video controls on a phone. */
const setSupportLauncherHidden = (hidden) => {
  document.body.classList.toggle(SUPPORT_LAUNCHER_CLASS, Boolean(hidden));
};

/** Early comments carry the analyst replies, so the thread reads oldest-first. */
const topLevelComments = (comments) =>
  (comments || [])
    .filter((comment) => comment.parentCommentId === null)
    .slice()
    .sort((a, b) => commentTime(a) - commentTime(b));

/** Replies stay in the order they were written, under that comment. */
const threadReplies = (comments, parentId) =>
  (comments || [])
    .filter((comment) => comment.parentCommentId === parentId)
    .slice()
    .sort((a, b) => commentTime(a) - commentTime(b));

class PostDetailCore extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      post: null,
      isLoading: true,
      currentComment: "",
      replyComment: "",
      commentReady: false,
      replyReady: false,
      commentSaving: false,
      savingReply: false,
      suggestedPosts: [],
      isFavorite: false,
      showShare: false,
      publicAccessVerified: null,
      postedReplyId: null,
    };
    this.shareRef = React.createRef();
    this.replyEditorRef = React.createRef();
    this.commentEditorRef = React.createRef();
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  componentDidMount = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    captureAffiliateFromSearch(window.location.search);
    this.fetchPost(this.props.match?.params?.postName);
    document.addEventListener("mousedown", this.handleClickOutside);
    window.addEventListener("storage", this.handleStorageChange);

    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
    if (urlParams.get("public-access-verified") !== null) {
      localStorage.setItem(
        "public-access-verified",
        urlParams.get("public-access-verified")
      );
    }
    if (urlParams.get("public-access-email") !== null) {
      localStorage.setItem(
        "public-access-email",
        urlParams.get("public-access-email")
      );
    }
    if (
      localStorage.getItem("public-access-verified") &&
      localStorage.getItem("public-access-verified") === "true"
    ) {
      this.setState({
        publicAccessVerified: true,
      });
    }
  };

  componentWillUnmount = () => {
    this._unmounted = true;
    setSupportLauncherHidden(false);
    if (this.postedReplyTimer) window.clearTimeout(this.postedReplyTimer);
    if (this._pinTimer) window.clearTimeout(this._pinTimer);
    document.removeEventListener("mousedown", this.handleClickOutside);
    window.removeEventListener("storage", this.handleStorageChange);
  };

  handleClickOutside = (event) => {
    if (this.shareRef && !this.shareRef.current?.contains(event.target)) {
      this.setState({
        showShare: false,
      });
    }
  };

  handleStorageChange = (e) => {
    if (e.key === "user" && e.newValue) {
      const updatedUser = JSON.parse(e.newValue);
      this.setState({
        user: updatedUser,
      });
    }
  };

  fetchPost = async (slug) => {
    if (!slug) {
      this.setState({ isLoading: false });
      return;
    }

    try {
      const resp = await getPostBySlug(slug);
      const suggested = [];
      const suggestedIds = (resp.data.suggestedPostIds || []).slice(0, 3);

      await Promise.all(
        suggestedIds.map(async (id) => {
          const postResp = await getPost(id);
          suggested.push(postResp.data);
        })
      );

      let user = null;
      let comments = null;
      let favorites = null;
      if (localStorage.getItem("user") !== null) {
        user = JSON.parse(localStorage.getItem("user"));

        const res = await getComments(resp.data.id);
        comments = res.data;
        favorites = await getFovuritePosts();
        favorites = favorites.data;
        resp.data.isFavourite =
          favorites.filter((f) => f.id === resp.data.id).length > 0;
        this.setState({
          isFavorite: resp.data.isFavourite,
        });
      }
      this.setState(
        {
          post: resp.data,
          comments: comments,
          user: user,
          favorites: favorites,
          suggestedPosts: suggested,
          isLoading: false,
        },
        () => {
          if (this._unmounted) return;
          setSupportLauncherHidden(resp.data?.contentTypeId === 2);
          const hash = window.location.hash?.replace("#", "");
          if (hash) {
            window.setTimeout(() => {
              document
                .getElementById(hash)
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 50);
          }
          setTimeout(() => {
            const mediaId = resp.data?.embeddedVideo?.mediaId;
            if (mediaId) {
              bindPostDetailWistia(mediaId);
            }
          }, 1000);
        }
      );
      pageVisit();
    } catch (error) {
      if (!this._unmounted) {
        setSupportLauncherHidden(false);
        this.setState({ isLoading: false });
      }
    }
  };

  noteCompose = (key, html) => {
    const ready = hasComposeContent(html);
    if (this.state[key] === ready) return;
    this.setState({ [key]: ready });
  };

  withOpenReplies = (incoming) => {
    const openIds = new Set(
      (this.state.comments || [])
        .filter((item) => item.replyOpen)
        .map((item) => item.id)
    );
    return (incoming || []).map((item) =>
      openIds.has(item.id) ? { ...item, replyOpen: true } : item
    );
  };

  releaseComposeFocus = (selector) => {
    const active = document.activeElement;
    const root = selector ? document.querySelector(selector) : null;
    if (
      active &&
      active !== document.body &&
      (!root || root.contains(active)) &&
      typeof active.blur === "function"
    ) {
      active.blur();
    }
  };

  pinScroll = (scrollY) => {
    const restore = () => {
      if (this._unmounted) return;
      if (Math.abs(window.scrollY - scrollY) > 1) {
        window.scrollTo(0, scrollY);
      }
    };
    restore();
    window.requestAnimationFrame(restore);
    if (this._pinTimer) window.clearTimeout(this._pinTimer);
    this._pinTimer = window.setTimeout(restore, 80);
  };

  focusReplyEditor = (commentId) => {
    const root = document.getElementById(String(commentId));
    const editable = root?.querySelector(".comment-compose .jodit-wysiwyg");
    if (!editable || typeof editable.focus !== "function") return;
    try {
      editable.focus({ preventScroll: true });
    } catch (error) {
      editable.focus();
    }
  };

  revealIfOffscreen = (id) => {
    const node = document.getElementById(id);
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const inView = rect.top >= 8 && rect.bottom <= window.innerHeight - 8;
    if (!inView) {
      node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  toggleComment = (commentId) => {
    const scrollY = window.scrollY;
    const current = (this.state.comments || []).find(
      (item) => item.id === commentId
    );
    const opening = !(current && current.replyOpen);
    if (!opening) this.releaseComposeFocus(".comment-compose");
    const comments = (this.state.comments || []).map((item) => {
      if (item.id === commentId) {
        return { ...item, replyOpen: opening };
      }
      if (opening && item.replyOpen) {
        return { ...item, replyOpen: false };
      }
      return item;
    });
    this.setState(
      {
        comments,
        replyComment: opening ? "" : this.state.replyComment,
        replyReady: opening ? false : this.state.replyReady,
      },
      () => {
        if (!opening) return;
        window.setTimeout(() => {
          if (this._unmounted) return;
          const box = document
            .getElementById(String(commentId))
            ?.querySelector(".comment-compose");
          if (!box) return;
          const rect = box.getBoundingClientRect();
          const inView = rect.top >= 8 && rect.bottom <= window.innerHeight - 8;
          if (inView) {
            this.pinScroll(scrollY);
          } else {
            box.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
          this.focusReplyEditor(commentId);
        }, 40);
      }
    );
  };

  cancelReply = (commentId) => {
    this.releaseComposeFocus(".comment-compose");
    this.clearComposeEditor(true);
    const comments = (this.state.comments || []).map((item) =>
      item.id === commentId ? { ...item, replyOpen: false } : item
    );
    this.setState({
      replyComment: "",
      replyReady: false,
      comments,
    });
  };

  componentDidUpdate(prevProps) {
    const pathname = this.props.location?.pathname;
    const prevPathname = prevProps.location?.pathname;

    if (!pathname || !prevPathname) {
      return;
    }

    const currentParams = getPostRouteParams(pathname);
    const prevParams = getPostRouteParams(prevPathname);

    if (
      currentParams.postName &&
      currentParams.postName !== prevParams.postName
    ) {
      this.setState({ isLoading: true, post: null });
      this.fetchPost(currentParams.postName);
      window.scrollTo(0, 0);
    }
  }

  reloadComments = async (postId) => {
    const res = await getComments(postId);
    this.setState({
      comments: this.withOpenReplies(res.data),
    });
  };

  addComment = async (comment) => {
    if (this._commentSaving) return;
    if (
      this.state.post &&
      this.state.post.currentUserProfile &&
      !this.state.post.currentUserProfile.isCommentingEnabled
    ) {
      NotificationService.error("Commenting disabled. Please contact support.");
      return;
    }

    const isReply = comment != null;
    const content = this.getComposeHtml(isReply);
    if (!hasComposeContent(content)) {
      return;
    }

    this._commentSaving = true;
    this.setState({ commentSaving: true, savingReply: isReply });

    const data = {
      postId: this.state.post.id,
      parentCommentId: comment,
      content,
    };

    try {
      const resp = await createComment(data);

      if (resp && resp.status && resp.status >= 400) {
        const errorMessage =
          resp?.data?.error || "Failed to post comment. Please try again.";
        NotificationService.error(errorMessage);
        if (!this._unmounted) this.setState({ commentSaving: false });
        return;
      }

      if (resp && resp.data) {
        const comments = [...(this.state.comments || []), resp.data];
        const postedId = resp.data.id;
        if (this.postedReplyTimer) window.clearTimeout(this.postedReplyTimer);
        if (isReply) this.releaseComposeFocus(".comment-compose");
        this.setState(
          {
            comments,
            currentComment: isReply ? this.state.currentComment : "",
            replyComment: isReply ? "" : this.state.replyComment,
            commentReady: isReply ? this.state.commentReady : false,
            replyReady: isReply ? false : this.state.replyReady,
            postedReplyId: isReply ? postedId : null,
            commentSaving: false,
          },
          () => {
            this.clearComposeEditor(isReply);
            if (isReply) {
              this.toggleComment(comment);
              this.postedReplyTimer = window.setTimeout(() => {
                if (this._unmounted) return;
                this.setState({ postedReplyId: null });
              }, 5000);
              return;
            }
            window.setTimeout(() => this.revealIfOffscreen(postedId), 50);
          }
        );
      } else if (!this._unmounted) {
        this.setState({ commentSaving: false });
      }
    } catch (error) {
      NotificationService.error("Failed to post comment. Please try again.");
      if (!this._unmounted) this.setState({ commentSaving: false });
    } finally {
      this._commentSaving = false;
    }
  };

  getComposeHtml = (isReply) => {
    const jodit = isReply ? this.replyJodit : this.commentJodit;
    if (jodit && typeof jodit.value === "string") {
      return jodit.value;
    }
    const ref = isReply ? this.replyEditorRef : this.commentEditorRef;
    const inst = ref && ref.current;
    if (inst && typeof inst.value === "string") {
      return inst.value;
    }
    const selector = isReply
      ? ".comment-compose__editor .jodit-wysiwyg"
      : ".comment-compose-card__editor .jodit-wysiwyg";
    const el = document.querySelector(selector);
    if (el) return el.innerHTML;
    return isReply
      ? this.state.replyComment || ""
      : this.state.currentComment || "";
  };

  clearComposeEditor = (isReply) => {
    const jodit = isReply ? this.replyJodit : this.commentJodit;
    if (jodit) {
      jodit.value = "";
    }
    const ref = isReply ? this.replyEditorRef : this.commentEditorRef;
    const inst = ref && ref.current;
    if (inst && inst !== jodit && typeof inst.value === "string") {
      inst.value = "";
    }
  };

  setCommentJodit = (editor) => {
    this.commentJodit = editor;
  };

  setReplyJodit = (editor) => {
    this.replyJodit = editor;
  };

  getReplyEditorConfig = (authorName) => {
    const key = authorName || "";
    // Rebuild when base editorConfig identity changes (module hot reload / fixes).
    if (
      !this._replyConfigCache ||
      this._replyConfigBase !== editorConfig
    ) {
      this._replyConfigCache = {};
      this._replyConfigBase = editorConfig;
    }
    if (!this._replyConfigCache[key]) {
      this._replyConfigCache[key] = {
        ...editorConfig,
        autofocus: false,
        placeholder: `Reply to ${key}…`,
      };
    }
    return this._replyConfigCache[key];
  };

  isCommentingDisabled = () => {
    const { post } = this.state;
    return (
      post &&
      post.currentUserProfile &&
      !post.currentUserProfile.isCommentingEnabled
    );
  };

  requestDeleteComment = (comment) => {
    if (!comment) return;
    this.setState({ commentPendingDelete: comment });
  };

  cancelDeleteComment = () => {
    if (this.state.commentDeleteBusy) return;
    this.setState({ commentPendingDelete: null });
  };

  confirmDeleteComment = async () => {
    const comment = this.state.commentPendingDelete;
    if (!comment || this.state.commentDeleteBusy) return;

    this.setState({ commentDeleteBusy: true });
    try {
      await removeComment(comment.id);
      await this.reloadComments(this.state.post.id);
      this.setState({
        commentPendingDelete: null,
        commentDeleteBusy: false,
      });
      showPostDetailToast("Comment deleted");
    } catch (error) {
      this.setState({ commentDeleteBusy: false });
      showPostDetailToast("This comment could not be deleted");
    }
  };

  addLike = async (comment) => {
    if (this._likeLock) return;
    this._likeLock = true;
    try {
      if (!comment.likes?.likedByCurrentUser) {
        await createLike(comment.id);
      } else {
        await removeLike(comment.id);
      }
      const res = await getComments(this.state.post.id);
      if (this._unmounted) return;
      this.setState({
        comments: this.withOpenReplies(res.data),
      });
    } catch (error) {
      showPostDetailToast("Couldn't update the like");
    } finally {
      this._likeLock = false;
    }
  };

  setCurrentComment = (text) => {
    this.noteCompose("commentReady", text);
  };

  setAsFavourite = () => {
    createFavoritePost(this.state.post.id);
    const p = this.state.post;
    p.isFavourite = !this.state.post.isFavourite;
    this.setState({
      post: p,
    });
  };

  removeAsFavorite = () => {
    removeFavoritePost(this.state.post.id);
    const p = this.state.post;
    p.isFavourite = !this.state.post.isFavourite;
    this.setState({
      post: p,
    });
  };

  showShare = () => {
    this.setState({
      showShare: !this.state.showShare,
    });
  };

  likePost = async () => {
    await createLikeOnPost(this.state.post.id);
    const temp = this.state.post;
    temp.likes.likedByCurrentUser = !temp.likes.likedByCurrentUser;
    if (temp.likes.likedByCurrentUser) {
      temp.likes.total = temp.likes.total + 1;
    } else {
      temp.likes.total = temp.likes.total - 1;
      await deleteLikeOnPost(this.state.post.id);
    }
    this.setState({
      post: temp,
    });
  };

  subscribeToPublic = async () => {
    const inviterAffiliateKey = getStoredAffiliateKey();
    const marketingSourceKey = getStoredMarketingKey();
    const url = new URL(window.location.href);
    const publicAccess = await createPublicAccess(
      this.state.publicAccessEmail,
      this.state.publicAccessName,
      inviterAffiliateKey,
      marketingSourceKey,
      `${url.origin}${url.pathname}?public-access-email=${
        this.state.publicAccessEmail
      }&public-access-verified=${true}`
    );
    if (publicAccess.data.error) {
      this.setState({
        publicAccessError: publicAccess.data.error,
      });
    }
    if (publicAccess.data.alreadyVerified) {
      localStorage.setItem("public-access-email", this.state.publicAccessEmail);
      localStorage.setItem("public-access-verified", true);
      this.setState({
        publicAccessVerified: true,
      });
    }
    if (!publicAccess.data.alreadyVerified) {
      localStorage.setItem("public-access-email", this.state.publicAccessEmail);
      localStorage.setItem("public-access-verified", false);
      this.setState({
        publicAccessVerified: false,
      });
    }
  };

  checkPublicAccessConfirmed = async () => {
    let email = null;
    if (this.state.publicAccessEmail) {
      email = this.state.publicAccessEmail;
    }
    if (localStorage.getItem("public-access-email")) {
      email = localStorage.getItem("public-access-email");
    }
    const access = await getPublicAccessCheck(email);
    localStorage.setItem("public-access-verified", access.data.verified);
    if (access.data.verified) {
      this.setState({
        publicAccessVerified: true,
      });
    }
  };

  onPublicNameInput = (event) => {
    this.setState({
      publicAccessName: event.target.value,
    });
  };

  onPublicEmailInput = (event) => {
    this.setState({
      publicAccessEmail: event.target.value,
    });
  };

  render() {
    const { comments, user, post, suggestedPosts } = this.state;
    const pathname = this.props.location?.pathname || "";
    const backNav = getBackNavigation(pathname);
    const categoryLabel =
      post?.tags?.[0]?.name ||
      post?.educationTopics?.[0]?.name ||
      (post?.contentTypeId === 2 ? "Video" : "Blog");

    const pendingDelete = this.state.commentPendingDelete;

    return (
      <PostDetailMain className="post-detail-page">
        {this.state.isLoading ? (
          <PostDetailLoading>
            <span aria-hidden="true" />
            Loading your content...
          </PostDetailLoading>
        ) : (
          <PostDetailArticle>
            {!post && !this.state.isLoading ? (
              <div style={{ padding: "48px 0", textAlign: "center" }}>
                <h1>Post not found</h1>
                <p style={{ color: "hsl(var(--hp-muted-foreground))" }}>
                  The post you&apos;re looking for doesn&apos;t exist or has been removed.
                </p>
                <Link to={backNav.to}>{backNav.label}</Link>
              </div>
            ) : (
              post && (
                <>
                  <PostDetailNav>
                    <PostDetailBreadcrumb>
                      <PostDetailBackLink to={backNav.to}>
                        <span className="material-icons" aria-hidden="true">
                          arrow_back
                        </span>
                        {backNav.feedLabel}
                      </PostDetailBackLink>
                      <PostDetailBreadcrumbSep>/</PostDetailBreadcrumbSep>
                      <PostDetailBreadcrumbCurrent>
                        {categoryLabel}
                      </PostDetailBreadcrumbCurrent>
                    </PostDetailBreadcrumb>

                    <PostDetailHeaderActions
                      post={post}
                      user={user}
                      likePost={this.likePost}
                      setAsFavourite={this.setAsFavourite}
                      removeAsFavorite={this.removeAsFavorite}
                      showShare={this.state.showShare}
                      onShare={this.showShare}
                      shareRef={this.shareRef}
                    />
                  </PostDetailNav>

                  <PostDetailContent
                    post={post}
                    user={user}
                    publicAccessVerified={this.state.publicAccessVerified}
                    publicAccessError={this.state.publicAccessError}
                    checkPublicAccessConfirmed={this.checkPublicAccessConfirmed}
                    subscribeToPublic={this.subscribeToPublic}
                    publicAccessName={this.state.publicAccessName}
                    publicAccessEmail={this.state.publicAccessEmail}
                    onPublicEmailInput={this.onPublicEmailInput}
                    onPublicNameInput={this.onPublicNameInput}
                    suggestedPosts={suggestedPosts}
                    commentsCount={comments?.length ?? 0}
                    commentsSlot={
                      shouldShowComments(post, user) ? (
                    <PostDetailCommentsSlot $nested className="post-overview">
                      <div className="add-comment post-comments">
                        <div className="comments-shell">
                          <header className="comments-shell__head">
                            <div className="comments-shell__title-row">
                              <h2 className="comments-shell__title">
                                <PostDetailMessageCircleIcon className="comments-shell__icon" />
                                Discussion
                                <span className="comments-shell__count">
                                  ({comments?.length ?? 0})
                                </span>
                              </h2>
                            </div>
                            <p className="comments-shell__desc">
                              Post your question here — {post.author?.name} and the
                              Bitcoin.live team respond directly on each post.
                            </p>
                          </header>

                          <div className="comments-shell__body">
                            <div
                              className={`comment-compose-card${
                                this.isCommentingDisabled()
                                  ? " comment-compose-card--disabled"
                                  : ""
                              }`}
                            >
                              <div className="comment-compose-card__body">
                                <CommentAvatar
                                  avatar={user ? user.avatar.small : null}
                                  name={user ? user.name : ""}
                                />
                                <div className="comment-compose-card__main">
                                  {this.isCommentingDisabled() ? (
                                    <div
                                      className="comment-compose-card__locked"
                                      role="status"
                                    >
                                      <span
                                        className="material-icons"
                                        aria-hidden="true"
                                      >
                                        lock
                                      </span>
                                      <div>
                                        <strong>
                                          Commenting is disabled for your account
                                        </strong>
                                        <p>
                                          Your commenting ability has been
                                          temporarily restricted. Please contact
                                          support for assistance.
                                        </p>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="comment-compose-card__editor post-detail-jodit">
                                      <JoditEditor
                                        id="addComment"
                                        ref={this.commentEditorRef}
                                        editorRef={this.setCommentJodit}
                                        config={editorConfig}
                                        tabIndex={1}
                                        onBlur={(newContent) =>
                                          this.setCurrentComment(newContent)
                                        }
                                        onChange={(newContent) =>
                                          this.setCurrentComment(newContent)
                                        }
                                      />
                                    </div>
                                  )}
                                  <div className="comment-compose-card__footer">
                                    {!this.isCommentingDisabled() && (
                                      <span className="comment-compose-card__hint">
                                        Members only · be respectful, stay on topic
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      className="post-detail-comments__submit"
                                      disabled={
                                        this.isCommentingDisabled() ||
                                        this.state.commentSaving ||
                                        !this.state.commentReady
                                      }
                                      title={
                                        this.isCommentingDisabled()
                                          ? "Commenting disabled for your account"
                                          : ""
                                      }
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => this.addComment(null)}
                                    >
                                      <PostDetailSendIcon />
                                      {this.state.commentSaving && !this.state.savingReply
                                        ? "Posting…"
                                        : "Post"}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="comments-thread comments comment-list">
                              {!!comments &&
                                topLevelComments(comments).length === 0 && (
                                  <div className="comments-empty">
                                    <PostDetailMessageCircleIcon className="comments-empty__icon" />
                                    <p className="comments-empty__text">
                                      No comments yet.
                                    </p>
                                    <p className="comments-empty__subtext">
                                      Be the first to start the discussion on this post.
                                    </p>
                                  </div>
                                )}
                              {!!comments &&
                                topLevelComments(comments).map((comment) => {
                                    const replies = threadReplies(
                                      comments,
                                      comment.id
                                    );

                                    return (
                                      <article
                                        className="comment-thread comment-card comment-item"
                                        id={comment.id}
                                        key={comment.id}
                                      >
                                        <CommentBlock
                                          comment={comment}
                                          user={user}
                                          canReply
                                          isReplyingDisabled={this.isCommentingDisabled()}
                                          onDelete={this.requestDeleteComment}
                                          onLike={this.addLike}
                                          onReply={this.toggleComment}
                                          showNotify
                                          layout="postDetail"
                                        />

                                        {comment.replyOpen &&
                                          !this.isCommentingDisabled() && (
                                          <div className="comment-compose">
                                            <div className="comment-compose__replying">
                                              <PostDetailReplyIcon />
                                              <span>
                                                Replying to{" "}
                                                <strong>
                                                  {comment.user?.name}
                                                </strong>
                                              </span>
                                              <button
                                                type="button"
                                                className="comment-compose__cancel"
                                                onClick={() =>
                                                  this.cancelReply(comment.id)
                                                }
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                            <CommentAvatar
                                              avatar={user ? user.avatar.small : null}
                                              name={user ? user.name : ""}
                                              size="sm"
                                            />
                                            <div className="comment-compose__main">
                                              <div className="comment-compose__editor post-detail-jodit">
                                                <JoditEditor
                                                  id={`reply-${comment.id}`}
                                                  ref={this.replyEditorRef}
                                                  editorRef={this.setReplyJodit}
                                                  config={this.getReplyEditorConfig(
                                                    comment.user?.name
                                                  )}
                                                  tabIndex={1}
                                                  onBlur={(newContent) =>
                                                    this.noteCompose(
                                                      "replyReady",
                                                      newContent
                                                    )
                                                  }
                                                  onChange={(newContent) =>
                                                    this.noteCompose(
                                                      "replyReady",
                                                      newContent
                                                    )
                                                  }
                                                />
                                              </div>
                                              <div className="comment-compose__footer">
                                                <span className="comment-compose__hint">
                                                  Members only · be respectful, stay on topic
                                                </span>
                                                <button
                                                  type="button"
                                                  className="post-detail-comments__submit"
                                                  disabled={
                                                    this.isCommentingDisabled() ||
                                                    this.state.commentSaving ||
                                                    !this.state.replyReady
                                                  }
                                                  title={
                                                    this.isCommentingDisabled()
                                                      ? "Commenting disabled for your account"
                                                      : ""
                                                  }
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() =>
                                                    this.addComment(comment.id)
                                                  }
                                                >
                                                  <PostDetailSendIcon />
                                                  {this.state.commentSaving &&
                                                  this.state.savingReply
                                                    ? "Posting…"
                                                    : "Reply"}
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        )}

                                        {replies.length > 0 && (
                                          <CommentReplies
                                            replies={replies}
                                            parentAuthorName={comment.user?.name}
                                            user={user}
                                            onDelete={this.requestDeleteComment}
                                            onLike={this.addLike}
                                            layout="postDetail"
                                            postedReplyId={this.state.postedReplyId}
                                          />
                                        )}
                                      </article>
                                    );
                                  })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </PostDetailCommentsSlot>
                      ) : null
                    }
                  />
                </>
              )
            )}
          </PostDetailArticle>
        )}
        {pendingDelete &&
          ReactDOM.createPortal(
            <HomepageThemeScope
              className="post-detail-confirm"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  this.cancelDeleteComment();
                }
              }}
            >
              <div
                className="post-detail-confirm__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="post-detail-delete-title"
              >
                <h2 id="post-detail-delete-title">Delete this comment?</h2>
                <p>This removes it from the discussion.</p>
                <div className="post-detail-confirm__actions">
                  <button
                    type="button"
                    className="post-detail-confirm__cancel"
                    onClick={this.cancelDeleteComment}
                    disabled={this.state.commentDeleteBusy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="post-detail-confirm__delete"
                    onClick={this.confirmDeleteComment}
                    disabled={this.state.commentDeleteBusy}
                  >
                    {this.state.commentDeleteBusy ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            </HomepageThemeScope>,
            document.body
          )}
      </PostDetailMain>
    );
  }
}

export default withRouter(PostDetailCore);
